import { useCallback, useMemo } from "react";
import { v6 as uuidv6 } from "uuid";
import {
  gridFilterModelSelector,
  GridRenderHeaderFilterProps,
  useGridApiContext,
  useGridSelector,
} from "@mui/x-data-grid-pro";
import {
  DateRange,
  DateRangePicker,
  SingleInputDateRangeField,
} from "@mui/x-date-pickers-pro";
import __ from "src/utils/Translations";

const DateRangeFilter = (props: GridRenderHeaderFilterProps) => {
  const { colDef } = props;
  const apiRef = useGridApiContext();
  const filterModel = useGridSelector(apiRef, gridFilterModelSelector);

  const currentFieldFilters = useMemo(
    () => filterModel.items?.filter(({ field }) => field === colDef.field),
    [colDef.field, filterModel.items]
  );

  const rangePickerValue = useMemo(
    () =>
      [
        currentFieldFilters.find(({ operator }) => operator === "onOrAfter")
          ?.value || null,
        currentFieldFilters.find(({ operator }) => operator === "onOrBefore")
          ?.value || null,
      ] as DateRange<Date>,
    [currentFieldFilters]
  );

  const handleChangeDateRangePicker = useCallback(
    (newValue: DateRange<Date>) => {
      const newFilters = [];
      const from = currentFieldFilters.find(
        ({ operator }) => operator === "onOrAfter"
      );
      const to = currentFieldFilters.find(
        ({ operator }) => operator === "onOrBefore"
      );

      const [newValueFrom] = newValue;
      let newValueTo = newValue[1];

      if (newValueFrom === null && newValueTo === null) {
        [from, to].filter(Boolean).forEach((item) => {
          apiRef.current?.deleteFilterItem(item!);
        });
        return;
      }

      if (newValueFrom && newValueTo && newValueFrom > newValueTo) {
        newValueTo = null;
      }

      if (newValueFrom !== null) {
        newFilters.push({
          ...(from || { id: uuidv6(), field: colDef.field }),
          operator: "onOrAfter",
          value: newValueFrom,
        });
      } else if (from) {
        apiRef.current?.deleteFilterItem(from);
      }

      if (newValueTo !== null) {
        const endOfDay = new Date(newValueTo);
        endOfDay.setHours(23, 59, 59, 999);

        newFilters.push({
          ...(to || { id: uuidv6(), field: colDef.field }),
          operator: "onOrBefore",
          value: endOfDay,
        });
      } else if (to) {
        apiRef.current?.deleteFilterItem(to);
      }

      if (newFilters.length > 0) {
        apiRef.current?.upsertFilterItems(newFilters);
      }
    },
    [apiRef, colDef.field, currentFieldFilters]
  );

  return (
    <DateRangePicker
      value={rangePickerValue}
      onChange={handleChangeDateRangePicker}
      localeText={{ start: __("Data od"), end: __("Data do") }}
      slots={{ field: SingleInputDateRangeField }}
      slotProps={{
        field: {
          clearable: true,
        },
        textField: {
          size: "small",
          variant: "outlined",
          label: __("Podaj zakres dat"),
          InputLabelProps: { shrink: true },
          fullWidth: true,
        },
      }}
    />
  );
};

export default DateRangeFilter;
